import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { IconCart, IconPlus, IconMinus, IconTrash, IconX, IconStar, IconPhone } from './icons';
import './PublicLayout.css';

// Bandeau des soirées fidélité en cours (points multipliés)
function EventBanner() {
  const { loyaltyEvents } = useStore();
  if (!loyaltyEvents || loyaltyEvents.length === 0) return null;

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const current = loyaltyEvents.find(e =>
    String(e.startDate).slice(0, 10) <= todayKey && String(e.endDate).slice(0, 10) >= todayKey
  );
  if (!current) return null;

  const serviceLabel = current.service === 'lunch' ? ' ce midi' : current.service === 'dinner' ? ' ce soir' : '';

  return (
    <div className="event-banner">
      <span className="event-banner-flame">🔥</span>
      <strong>{current.name}</strong> — points de fidélité x{parseFloat(current.multiplier)}{serviceLabel} !
      <Link to="/fidelite" className="event-banner-link">J'en profite</Link>
    </div>
  );
}

function CartDrawer({ open, onClose }) {
  const { cart, cartTotal, cartCount, pointsReserved, updateQuantity, updateNotes, removeFromCart } = useStore();
  const navigate = useNavigate();

  const notesPlaceholder = (category) => {
    const c = (category || '').toLowerCase();
    if (c.includes('burger')) return 'Cuisson, option végé, sans oignons…';
    if (c.includes('enfant')) return 'Nuggets ou corn dog…';
    return 'Une précision pour la cuisine ?';
  };

  return (
    <>
      <div className={`drawer-overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <aside className={`cart-drawer ${open ? 'open' : ''}`} aria-label="Panier">
        <div className="cart-drawer-head">
          <h3>Mon panier</h3>
          <button className="drawer-close" onClick={onClose} aria-label="Fermer le panier">
            <IconX size={20} />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="cart-drawer-vide">
            <span className="cart-vide-emoji">🍔</span>
            <p>Votre panier est vide.</p>
            <button className="btn btn-or" onClick={() => { onClose(); navigate('/'); }}>
              Voir la carte
            </button>
          </div>
        ) : (
          <>
            <div className="cart-drawer-body">
              {cart.map((item) => (
                <div key={item.uid} className={`cart-ligne ${item.redeemed ? 'cart-ligne-reward' : ''}`}>
                  <div className="cart-ligne-haut">
                    <span className="cart-ligne-nom">
                      {item.redeemed && <IconStar size={14} filled />} {item.name}
                    </span>
                    <span className={item.redeemed ? 'cart-ligne-offert' : 'cart-ligne-prix'}>
                      {item.redeemed ? 'Offert' : `${(parseFloat(item.price) * item.quantity).toFixed(2)}€`}
                    </span>
                  </div>
                  <div className="cart-ligne-bas">
                    <div className="qty-controle">
                      <button onClick={() => updateQuantity(item.uid, item.quantity - 1)} aria-label="Une de moins">
                        <IconMinus size={16} />
                      </button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.uid, item.quantity + 1)} aria-label="Une de plus">
                        <IconPlus size={16} />
                      </button>
                    </div>
                    {item.redeemed ? (
                      <span className="cart-ligne-pts">{item.points_price * item.quantity} pts</span>
                    ) : null}
                    <button
                      className="cart-ligne-suppr"
                      onClick={() => removeFromCart(item.uid)}
                      aria-label={`Retirer ${item.name}`}
                      title="Retirer du panier"
                    >
                      <IconTrash size={17} />
                    </button>
                  </div>
                  {!item.redeemed && (
                    <input
                      type="text"
                      className="cart-ligne-notes"
                      placeholder={notesPlaceholder(item.category)}
                      value={item.notes || ''}
                      onChange={(e) => updateNotes(item.uid, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="cart-drawer-foot">
              {pointsReserved > 0 && (
                <div className="cart-drawer-pts">
                  <IconStar size={15} filled /> {pointsReserved} points utilisés pour vos articles offerts
                </div>
              )}
              <div className="cart-drawer-total">
                <span>{cartCount} article{cartCount > 1 ? 's' : ''}</span>
                <strong>{cartTotal.toFixed(2)}€</strong>
              </div>
              <button
                className="btn btn-or btn-block btn-lg"
                onClick={() => { onClose(); navigate('/commande'); }}
              >
                Finaliser ma commande
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function PublicLayout() {
  const { cartCount, cartTotal, customer } = useStore();
  const [cartOpen, setCartOpen] = useState(false);
  const [cartBump, setCartBump] = useState(false);
  const location = useLocation();
  const prevCount = useRef(cartCount);

  // Fermer le tiroir à chaque navigation
  useEffect(() => { setCartOpen(false); }, [location.pathname]);

  // Petit rebond du badge quand un article est ajouté
  useEffect(() => {
    if (cartCount > prevCount.current) {
      setCartBump(true);
      const t = setTimeout(() => setCartBump(false), 650);
      return () => clearTimeout(t);
    }
    prevCount.current = cartCount;
  }, [cartCount]);

  useEffect(() => { prevCount.current = cartCount; }, [cartCount]);

  return (
    <div className="site">
      <header className="site-header">
        <div className="site-header-inner">
          <nav className="site-nav">
            <NavLink to="/" end>La carte</NavLink>
            <NavLink to="/reserver">Réserver</NavLink>
            <NavLink to="/fidelite" className="nav-fidelite">
              <IconStar size={15} filled={Boolean(customer)} />
              {customer ? `${customer.balance} pts` : 'Fidélité'}
            </NavLink>
          </nav>

          <button
            className={`nav-panier ${cartCount > 0 ? 'plein' : ''} ${cartBump ? 'bump' : ''}`}
            onClick={() => setCartOpen(true)}
            aria-label={`Ouvrir le panier (${cartCount} article${cartCount > 1 ? 's' : ''})`}
          >
            <IconCart size={21} />
            {cartCount > 0 && <span className="nav-panier-count">{cartCount}</span>}
            {cartCount > 0 && <span className="nav-panier-total">{cartTotal.toFixed(2)}€</span>}
          </button>
        </div>
      </header>

      <EventBanner />

      <main className="site-main">
        <Outlet context={{ openCart: () => setCartOpen(true) }} />
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="footer-col footer-marque">
            <img src="/logo.svg" alt="Le Fief" className="footer-logo" />
            <p className="footer-tagline">American Food & Bar</p>
            <p className="footer-texte">
              Cuisine américaine authentique au cœur de Lacaze, produits locaux
              de qualité, fléchettes et ambiance conviviale.
            </p>
          </div>

          <div className="footer-col">
            <h3>Nous trouver</h3>
            <p>7 rue du Pont Vieux<br />81330 Lacaze, Tarn</p>
            <a href="tel:0749110186" className="footer-tel">
              <IconPhone size={15} /> 07 49 11 01 86
            </a>
          </div>

          <div className="footer-col">
            <h3>En un clic</h3>
            <Link to="/">La carte & commande</Link>
            <Link to="/reserver">Réserver une table</Link>
            <Link to="/fidelite">Programme de fidélité</Link>
          </div>

          <div className="footer-col footer-galerie">
            <img src="/canape.png" alt="Ambiance du Fief" />
            <img src="/flechettes.png" alt="Fléchettes" />
            <img src="/salle.png" alt="La salle" />
          </div>
        </div>
        <div className="footer-bas">
          © {new Date().getFullYear()} Le Fief — Tous droits réservés · Paiement sur place : CB, espèces, chèques & chèques vacances
          <span className="footer-sep">·</span>
          <Link to="/admin" className="footer-admin">Espace restaurateur</Link>
        </div>
      </footer>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}

export default PublicLayout;
